import { useEffect, useState } from 'react';
import { ResetPasswordScreen } from '@crave/ui';
import { onSession, requestPasswordReset, supabase, updatePassword } from '../lib/auth.ts';

type LinkType = 'recovery' | 'invite' | 'signup' | 'email' | 'magiclink' | 'email_change';
const LINK_TYPES: LinkType[] = [
  'recovery',
  'invite',
  'signup',
  'email',
  'magiclink',
  'email_change',
];

/**
 * Where password links land: reset emails, and set-password links the owner shares with new staff.
 * The link signs the person in; they then choose a password.
 */
export function ResetPasswordPage() {
  const [state, setState] = useState<'checking' | 'ready' | 'invalid'>('checking');
  const [reason, setReason] = useState<string | null>(null);
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const description = query.get('error_description') ?? hash.get('error_description');
    if (description) setReason(description.replace(/\+/g, ' '));
    let settled = false;
    const markReady = () => {
      settled = true;
      setState('ready');
      window.history.replaceState(null, '', '/reset-password');
    };
    const tokenHash = query.get('token_hash');
    const type = query.get('type') as LinkType | null;
    const accessToken = hash.get('access_token');
    const refreshToken = hash.get('refresh_token');
    if (supabase && tokenHash && type && LINK_TYPES.includes(type)) {
      void supabase.auth.verifyOtp({ token_hash: tokenHash, type }).then(({ error }) => {
        if (error) {
          setReason(error.message);
          setState('invalid');
        } else markReady();
      });
      return;
    }
    if (supabase && accessToken && refreshToken) {
      void supabase.auth
        .setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(({ error }) => {
          if (error) {
            setReason(error.message);
            setState('invalid');
          } else markReady();
        });
      return;
    }
    return onSession((s) => {
      if (s) markReady();
      else if (!settled) setState('invalid');
    });
  }, []);
  return (
    <ResetPasswordScreen
      logoSrc="/brand/logo-color-480.png"
      state={state}
      reason={reason}
      onUpdate={async (password) => {
        const r = await updatePassword(password);
        if (r.needsCode)
          return 'For security, sign in again with an emailed code, then change your password.';
        return r.error;
      }}
      onRequestLink={requestPasswordReset}
      continueHref="/"
      continueLabel="Go to the kitchen queue"
    />
  );
}
